import {
  IssueFieldMultiSelectValueTokens,
  IssueFieldSingleSelectValueToken,
} from '@github-ui/issue-metadata/IssueFieldSingleSelectValueToken'
import {Link} from '@primer/react'

import {LABELS} from '../constants/labels'
import {IssueEventRationale} from './IssueEventRationale'
import type {IssueFieldAddedEventFragment} from './IssueFieldAddedEvent'
import type {IssueFieldChangedEventFragment} from './IssueFieldChangedEvent'
import {getSearchLink, normalizeMultiSelectOptions, parseFallbackMultiSelectOptions} from './IssueFieldEvent'
import styles from './IssueFieldEvent.module.css'
import type {IssueFieldRemovedEventFragment} from './IssueFieldRemovedEvent'

type IssueFieldEventKey = IssueFieldAddedEventFragment | IssueFieldChangedEventFragment | IssueFieldRemovedEventFragment

type RolledupIssueFieldEventProps = {
  rollupGroup: Record<string, IssueFieldEventKey[]>
  repositoryNameWithOwner?: string
}

/**
 * Renders a bundled summary of multiple issue field changes.
 *
 * Example output: "updated Product pillar Copilot Intelligence Platform, Trending At risk and Deliverable Staffship/Internal Release, and also removed Eng Staffing"
 */
export function RolledupIssueFieldEvent({
  rollupGroup,
  repositoryNameWithOwner,
}: RolledupIssueFieldEventProps): React.ReactElement | null {
  const addedEvents = (rollupGroup['IssueFieldAddedEvent'] as IssueFieldAddedEventFragment[]) || []
  const changedEvents = (rollupGroup['IssueFieldChangedEvent'] as IssueFieldChangedEventFragment[]) || []
  const removedEvents = (rollupGroup['IssueFieldRemovedEvent'] as IssueFieldRemovedEventFragment[]) || []

  const hasUpdated = addedEvents.length > 0 || changedEvents.length > 0
  const hasRemoved = removedEvents.length > 0
  const hasContent = hasUpdated || hasRemoved

  if (!hasContent) {
    return null
  }

  return (
    <>
      {hasUpdated && (
        <>
          {`${LABELS.timeline.issueFieldRollup.updated} `}
          <UpdatedFieldsList
            addedEvents={addedEvents}
            changedEvents={changedEvents}
            repositoryNameWithOwner={repositoryNameWithOwner}
          />
        </>
      )}
      {hasUpdated && hasRemoved && `, ${LABELS.timeline.issueFieldRollup.andAlso} `}
      {hasRemoved && (
        <>
          {`${LABELS.timeline.issueFieldRollup.removed} `}
          <RemovedFieldsList removedEvents={removedEvents} />
        </>
      )}{' '}
    </>
  )
}

type UpdatedFieldsListProps = {
  addedEvents: IssueFieldAddedEventFragment[]
  changedEvents: IssueFieldChangedEventFragment[]
  repositoryNameWithOwner?: string
}

function UpdatedFieldsList({addedEvents, changedEvents, repositoryNameWithOwner}: UpdatedFieldsListProps) {
  const totalCount = addedEvents.length + changedEvents.length

  return (
    <>
      {addedEvents.map((eventKey, index) => {
        const isLastOverall = index === totalCount - 1
        const isSecondToLastOverall = index === totalCount - 2
        const eventIndex = (eventKey as {__index?: number}).__index ?? index
        return (
          <AddedFieldItem
            key={`added-${eventIndex}`}
            eventKey={eventKey}
            repositoryNameWithOwner={repositoryNameWithOwner}
            isLast={isLastOverall}
            isSecondToLast={isSecondToLastOverall}
          />
        )
      })}
      {changedEvents.map((eventKey, index) => {
        const globalIndex = addedEvents.length + index
        const isLastOverall = globalIndex === totalCount - 1
        const isSecondToLastOverall = globalIndex === totalCount - 2
        const eventIndex = (eventKey as {__index?: number}).__index ?? index
        return (
          <ChangedFieldItem
            key={`changed-${eventIndex}`}
            eventKey={eventKey}
            repositoryNameWithOwner={repositoryNameWithOwner}
            isLast={isLastOverall}
            isSecondToLast={isSecondToLastOverall}
          />
        )
      })}
    </>
  )
}

type RemovedFieldsListProps = {
  removedEvents: IssueFieldRemovedEventFragment[]
}

function RemovedFieldsList({removedEvents}: RemovedFieldsListProps) {
  return (
    <>
      {removedEvents.map((eventKey, index) => {
        const eventIndex = (eventKey as {__index?: number}).__index ?? index
        return (
          <RemovedFieldItem
            key={`removed-${eventIndex}`}
            eventKey={eventKey}
            isLast={index === removedEvents.length - 1}
            isSecondToLast={index === removedEvents.length - 2}
          />
        )
      })}
    </>
  )
}

type AddedFieldItemProps = {
  eventKey: IssueFieldAddedEventFragment
  repositoryNameWithOwner?: string
  isLast: boolean
  isSecondToLast: boolean
}

function AddedFieldItem({eventKey, repositoryNameWithOwner, isLast, isSecondToLast}: AddedFieldItemProps) {
  const data = eventKey

  if (!data.issueField?.name || !data.issueField?.dataType) return null

  const separator = isLast ? '' : isSecondToLast ? ` ${LABELS.timeline.and} ` : ', '
  const multiSelectValue = data.options?.map(option => option.name).join(', ')
  const searchLink = getSearchLink(repositoryNameWithOwner, data.issueField.name, data.value ?? multiSelectValue)
  const isSingleSelect = data.issueField.dataType === 'SINGLE_SELECT'
  const isMultiSelect = data.issueField.dataType === 'MULTI_SELECT'
  const normalizedOptions = isMultiSelect
    ? data.options?.length
      ? normalizeMultiSelectOptions(data.options)
      : parseFallbackMultiSelectOptions(data.value, data.color)
    : []

  return (
    <span className={styles.issueFieldRollupValueWrapper}>
      <span className={styles.rollupFieldName}>{data.issueField.name}</span>
      {isMultiSelect && normalizedOptions.length > 0 ? (
        <span>
          {' '}
          <Link href={searchLink} className={styles.inlineToken}>
            <IssueFieldMultiSelectValueTokens
              options={normalizedOptions}
              getTooltipText={() => undefined}
              containerClassName={styles.inlineMultiContainer}
            />
          </Link>
        </span>
      ) : data.value ? (
        isSingleSelect ? (
          <span>
            {' '}
            <Link href={searchLink} className={styles.inlineToken}>
              <IssueFieldSingleSelectValueToken
                name={data.value}
                color={data.color || ''}
                getTooltipText={() => undefined}
              />
            </Link>
          </span>
        ) : (
          <span>
            {' '}
            <Link href={searchLink} inline className={styles.issueFieldValueLink}>
              {data.value}
            </Link>
          </span>
        )
      ) : null}
      {data.intent?.rationale && (
        <IssueEventRationale
          rationale={data.intent.rationale}
          issueEventDatabaseId={String(data.databaseId)}
          analyticsAction="ISSUE_FIELD_ADDED"
        />
      )}
      {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
      {separator}
    </span>
  )
}

type ChangedFieldItemProps = {
  eventKey: IssueFieldChangedEventFragment
  repositoryNameWithOwner?: string
  isLast: boolean
  isSecondToLast: boolean
}

function ChangedFieldItem({eventKey, repositoryNameWithOwner, isLast, isSecondToLast}: ChangedFieldItemProps) {
  const data = eventKey

  if (!data.issueField?.name || !data.issueField?.dataType) return null

  const separator = isLast ? '' : isSecondToLast ? ` ${LABELS.timeline.and} ` : ', '
  const multiSelectValue = data.newOptions?.map(option => option.name).join(', ')
  const searchLink = getSearchLink(repositoryNameWithOwner, data.issueField.name, data.newValue ?? multiSelectValue)
  const isSingleSelect = data.issueField.dataType === 'SINGLE_SELECT'
  const isMultiSelect = data.issueField.dataType === 'MULTI_SELECT'
  const normalizedOptions = isMultiSelect
    ? data.newOptions?.length
      ? normalizeMultiSelectOptions(data.newOptions)
      : parseFallbackMultiSelectOptions(data.newValue, data.newColor)
    : []

  return (
    <span className={styles.issueFieldRollupValueWrapper}>
      <span className={styles.rollupFieldName}>{data.issueField.name}</span>
      {isMultiSelect && normalizedOptions.length > 0 ? (
        <span>
          {' '}
          <Link href={searchLink} className={styles.inlineToken}>
            <IssueFieldMultiSelectValueTokens
              options={normalizedOptions}
              getTooltipText={() => undefined}
              containerClassName={styles.inlineMultiContainer}
            />
          </Link>
        </span>
      ) : data.newValue ? (
        isSingleSelect ? (
          <span>
            {' '}
            <Link href={searchLink} className={styles.inlineToken}>
              <IssueFieldSingleSelectValueToken
                name={data.newValue}
                color={data.newColor || ''}
                getTooltipText={() => undefined}
              />
            </Link>
          </span>
        ) : (
          <span>
            {' '}
            <Link href={searchLink} inline className={styles.issueFieldValueLink}>
              {data.newValue}
            </Link>
          </span>
        )
      ) : null}
      {data.intent?.rationale && (
        <IssueEventRationale
          rationale={data.intent.rationale}
          issueEventDatabaseId={String(data.databaseId)}
          analyticsAction="ISSUE_FIELD_CHANGED"
        />
      )}
      {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
      {separator}
    </span>
  )
}

type RemovedFieldItemProps = {
  eventKey: IssueFieldRemovedEventFragment
  isLast: boolean
  isSecondToLast: boolean
}

function RemovedFieldItem({eventKey, isLast, isSecondToLast}: RemovedFieldItemProps) {
  const data = eventKey

  if (!data.issueField?.name) return null

  const separator = isLast ? '' : isSecondToLast ? ` ${LABELS.timeline.and} ` : ', '

  return (
    <span className={styles.issueFieldRollupValueWrapper}>
      <span className={styles.rollupFieldName}>{data.issueField.name}</span>
      {data.intent?.rationale && (
        <IssueEventRationale
          rationale={data.intent.rationale}
          issueEventDatabaseId={String(data.databaseId)}
          analyticsAction="ISSUE_FIELD_REMOVED"
        />
      )}
      {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
      {separator}
    </span>
  )
}
